import React from "react";
import { Lock, Filter, Layers, SlidersHorizontal, ShieldCheck } from "lucide-react";

const layers = [
  {
    icon: Filter,
    title: "Identifier filters",
    body:
      "Blacklists and device-type (TAC) filters applied at ingest, before anything else touches the data.",
  },
  {
    icon: Lock,
    title: "Hash with rotating salt",
    body:
      "Subscriber identifiers are replaced with rotating-salt hashes that cannot be reversed to the original IDs.",
  },
  {
    icon: Layers,
    title: "Spatio-temporal aggregation",
    body:
      "Events compressed into spatial cells and time buckets. The unit of analysis stops being a person.",
  },
  {
    icon: SlidersHorizontal,
    title: "Configurable thresholds",
    body:
      "k-anonymity (k>5 by default), differential privacy, or operator-specific rules calibrated to each operator's policy.",
  },
  {
    icon: ShieldCheck,
    title: "Automated validation",
    body:
      "Every dataset is audited at the egress boundary. Only datasets that pass the operator's policy checks are released.",
  },
];

const PrivacySection = () => {
  return (
    <section id="privacy" className="py-24 bg-paper">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-12 mb-12">
          <div className="lg:col-span-7">
            <p className="text-xs font-semibold tracking-widest text-brand-600 uppercase mb-3">
              Privacy by architecture
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-ink tracking-tight mb-4">
              Sensitive data never leaves the operator.
            </h2>
            <p className="text-lg text-ink-muted leading-relaxed">
              Three isolated environments. A configurable anonymisation
              pipeline that the operator's CISO and DPO control. An automated
              validation pass before any data crosses the operator boundary.
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-paper-200 border border-paper-300 rounded-lg p-6">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-ink-subtle mb-3">
                Industry comparison
              </p>
              <div className="space-y-3 text-sm">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-ink-muted">Eurostat multi-MNO ref</span>
                  <span className="font-mono text-xs text-ink-subtle">hashing only</span>
                </div>
                <div className="h-px bg-paper-300" />
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-semibold text-ink">Telcofy</span>
                  <span className="font-mono text-xs text-brand-700">
                    full stack + per-operator config
                  </span>
                </div>
              </div>
              <p className="text-xs text-ink-subtle leading-relaxed mt-4">
                Pseudonymisation alone leaves data inside GDPR scope. The
                Telcofy pipeline is built to clear the GDPR Recital 26
                anonymisation threshold under each operator's chosen policy.
              </p>
            </div>
          </div>
        </div>

        {/* Pipeline — five stages of one process, connected. */}
        <div className="bg-paper border border-paper-300 rounded-lg p-6 md:p-10">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-ink-subtle mb-8">
            Pipeline · all stages run inside the operator boundary
          </p>
          <ol className="relative grid md:grid-cols-5 gap-y-8 gap-x-2">
            {/* Connector line — desktop only, runs through the number row */}
            <div
              className="hidden md:block absolute left-[10%] right-[10%] top-[18px] h-px bg-paper-300"
              aria-hidden="true"
            />
            {layers.map(({ icon: Icon, title, body }, idx) => (
              <li key={title} className="relative">
                {/* Number badge — sits on the connector line */}
                <div className="flex items-center justify-center md:justify-start mb-4 md:mb-6 relative">
                  <div className="w-9 h-9 rounded-full bg-paper border-2 border-brand-500 grid place-items-center font-mono text-xs font-semibold text-brand-700 z-10">
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={14} className="text-ink-subtle" />
                  <h3 className="font-display text-sm font-semibold text-ink leading-tight">
                    {title}
                  </h3>
                </div>
                <p className="text-xs text-ink-muted leading-relaxed">
                  {body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

export default PrivacySection;
